<template>
  <button
    class="category"
    type="button"
    v-bind:data-category-id="category.id"
    v-bind:data-category-name="category.name"
    v-bind:id="category.id"
    v-bind:data-status="defineStatus"
    v-bind:data-parent-category-id="category.parentCategoryId"
    v-bind:draggable="isDraggableElement"
    v-on:dragend="onDrop"
    v-on:dragenter="onDrag"
    v-on:dragover.prevent
    v-on:click="dispatchSelectCategory(category.id, category.priceId)">
    <button
      v-if="isShowButtonDrag"
      class="category__drag-handle"
      type="button"
      title="Перенести"
      v-on:mousedown="isButtonDrag = true"
      v-on:mouseup="isButtonDrag = false"
    />
    <div class="category__row">
      <div
        v-bind:data-category-id="category.id"
        v-bind:data-parent-category-id="category.parentCategoryId"
        v-bind:data-category-name="category.name"
        v-bind:data-status="defineStatus"
        class="category-name"
        v-bind:class="{ selected: isSelected, subcategory: category.parentCategoryId }">
        {{ category.name }}
      </div>
      <button
        v-if="hasEdit"
        class="category__icon-edit"
        type="button"
        title="Редактировать"
        v-on:click.stop="dispatchEditCategory(category.id, category.priceId)" />
      <button
        v-if="hasDelete"
        class="category__icon-delete"
        type="button"
        title="Удалить"
        v-on:click.stop="dispatchDeleteCategory(category.id, category.priceId)" />
    </div>
  </button>
</template>

<script>
import mobileMixin from '@/mixins/mobileMixin';

/**
 * Меняем порядок категорий
 *
 * @type {string}
 */
const CHANGE_CATEGORY_ORDER = 'change-order-categories';

/**
 * @description Элемент категории услуги.
 * @type {any}
 */
export default {
  name: 'CategoryItem',
  mixins: [
    mobileMixin,
  ],
  props: {
    /**
     * Включить возможность drag and drop
     * @type {boolean}
     */
    isDrag: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    /**
     * Выбранный элемент методом drag
     * @type {boolean}
     */
    dragSelectedElement: {
      type: HTMLElement,
      required: false,
      default: () => {},
    },
    /**
     * @description Категория.
     * @type {{id:Number,name:String}}
     */
    category: {
      type: Object,
      required: true,
    },
    /**
     * @description Идентификатор выбранной категории.
     * @type {number|string}
     */
    selectedCategory: {
      type: [Number, String],
      required: true,
    },
    /**
     * @description Имеет право удалять услуги.
     * @type {boolean}
     */
    hasDelete: Boolean,
    /**
     * @description Имеет право редактировать услуги.
     * @type {boolean}
     */
    hasEdit: Boolean,
  },
  computed: {
    /**
     * Определить статус категории. "Parent" или "child".
     *
     * @return {string}
     */
    defineStatus() {
      return this.category.parentCategoryId ? 'child' : 'parent';
    },
    /**
     * @description Выбран ли элемент.
     * @returns {boolean}
     */
    isSelected(self) {
      return self.category.id === self.selectedCategory;
    },
    /**
     * @description Показывать ли кнопку DnD.
     * @returns {boolean}
     */
    isShowButtonDrag() {
      return this.isDrag && !this.isMobile;
    },
    /**
     * @description Активировано ли перемещение элемента.
     * @returns {boolean}
     */
    isDraggableElement() {
      return this.isDrag && (this.isMobile || this.isButtonDrag);
    },
  },
  data() {
    return {
      enteredElementId: null,
      isButtonDrag: false,
    };
  },
  watch: {
    /**
     * Обработать события изменения элемента на который
     * навились
     *
     * @param {HTMLElement} el
     */
    dragSelectedElement(el) {
      if (el.classList.contains('category-name')) {
        el.classList.add('category__drag-selected-element');
      }
    },
  },
  methods: {
    /**
     * @description Отправить событие редактирования категории.
     * @param {number} id Идентификатор категории.
     * @param {number|null} priceId Идентификатор Прайса.
     * @returns {void}
     */
    dispatchEditCategory(id, priceId) {
      this.$emit('edit-category', id, priceId);
    },
    /**
     * @description Отправить событие удаления категории.
     * @param {number} id Идентификатор категории
     * @param {number|null} priceId Идентификатор Прайса.
     * @returns {void}
     */
    dispatchDeleteCategory(id, priceId) {
      this.$emit('delete-category', id, priceId);
    },
    /**
     * @description Отправить событие выбора категории.
     * @param {number} id Идентификатор категории
     * @param {number|null} priceId Идентификатор Прайса.
     * @returns {void}
     */
    dispatchSelectCategory(id, priceId) {
      if (id !== this.selectedCategory) {
        this.$emit('select-category', id, priceId);
      }
    },
    /**
     * @description Отправить событие вставить элемент на который навились методом drag.
     * @param {event} event Элемент categoryItem.
     * @returns {void}
     */
    dispatchSetDragSelectElement(event) {
      const { clientX, clientY } = event;
      const element = document.elementFromPoint(clientX, clientY);
      if (!event.target.classList.contains('category__drag-handle')) {
        this.$emit('set-drag-select', element);
      }
    },
    /**
     * @description Обработать события начала перетаскивания элемента услуги.
     *
     * @param {event} event
     */
    onDrag(event) {
      this.disableDragSelectedStyle();
      this.dispatchSetDragSelectElement(event);
    },
    /**
     * @description Отключить стили для прежде наведенных элементов
     *
     * @return {void}
     */
    disableDragSelectedStyle() {
      document.querySelectorAll('.category__drag-selected-element')
        .forEach((el) => {
          el.classList.remove('category__drag-selected-element');
        });
    },
    /**
     * @description Обработать события при определении нового места для выбранного категории.
     * @param {event} event
     * @return {void}
     */
    onDrop(event) {
      const selectedCategoryStatus = event.target.getAttribute('data-status');
      const enteredCategoryStatus = this.dragSelectedElement.getAttribute('data-status');
      if (selectedCategoryStatus === 'child' && enteredCategoryStatus === 'child') {
        this.dropForChild(event);
      }
      if (selectedCategoryStatus === 'parent' && enteredCategoryStatus === 'parent') {
        this.dropForParent(event);
      }
      this.isButtonDrag = false;
    },
    /**
     * @decrioption Обработать событие "drop" для компонентов со статусом "parent".
     * @param {event} event
     * @returns {void}
     */
    dropForParent(event) {
      const enteredCategoryId = this.dragSelectedElement.getAttribute('data-category-id');
      const selectedCategoryId = event.target.getAttribute('data-category-id');
      this.$emit(CHANGE_CATEGORY_ORDER, {
        selectedElementId: selectedCategoryId,
        enteredElementId: enteredCategoryId,
      });
      this.dragSelectedElement.classList.remove('category__drag-selected-element');
    },
    /**
     * @decrioption Обработать событие "drop" для компонентов со статусом "child".
     * @param {event} event
     * @returns {void}
     */
    dropForChild(event) {
      const enteredCategoryId = this.dragSelectedElement.getAttribute('data-category-id');
      const enteredParentCategoryId = this.dragSelectedElement.getAttribute('data-parent-category-id');
      const selectedCategoryId = event.target.getAttribute('data-category-id');
      const selectedParentCategoryId = event.target.getAttribute('data-parent-category-id');
      if (enteredParentCategoryId === selectedParentCategoryId) {
        this.$emit(CHANGE_CATEGORY_ORDER, {
          selectedElementId: selectedCategoryId,
          enteredElementId: enteredCategoryId,
        });
        this.dragSelectedElement.classList.remove('category__drag-selected-element');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.checked.category::before {
  content: "";
  display: block;
  float: right;
  width: 24px;
  height: 24px;
  margin: 5px 5px 0 0;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 70%;
  background-image: url("@/assets/img/checked/checked-grey.svg");
  pointer-events: none;
}
.category {
  display: inline-flex;
  align-items: center;
  text-align: left;
  cursor: default;
  &:has(.category-name:only-child) {
    padding: 0;
  }
  &__drag-selected-element {
    box-shadow: 0px -2px 0px 0px #0489e0;
  }
  &__row {
    position: relative;
    display: flex;
    flex-direction: row;
    gap: 10px;
    flex-grow: 1;
  }
  &__drag-handle {
    flex-shrink: 0;
    width: 18px;
    height: 17px;
    margin-inline: 2px;
    padding: 10px;
    mask-image: url("@/assets/img/drag-indicator.svg");
    mask-repeat: no-repeat;
    mask-position: center center;
    mask-size: contain;
    visibility: hidden;
    background-color: #90A0B7;
    cursor: grab;
    &:hover {
      background-color: var(--action-color);
    }
    &:active {
      cursor: grabbing;
    }
  }
  &:not(:only-child):hover .category__drag-handle {
    visibility: visible;
  }
  &__icon {
    &-edit {
      align-self: center;
      background-image: url("@/assets/img/pen-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 14px;
      height: 14px;
      padding: 0;
      margin: 0;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/pen-blue.svg");
      }
    }
    &-delete {
      align-self: center;
      background-image: url("@/assets/img/exit-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 12px;
      height: 12px;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
      }
    }
  }
}
.category > .category-items {
  font-size: 90%;
  padding-left: 5px;
  background: #eaeef2;
}
.category-name {
  flex: 1;
  cursor: pointer;
  font-size: 14px;
  line-height: 17px;
  overflow-wrap: anywhere;
  hyphens: auto;
  &:hover {
    color: #0489e0;
  }
}
.selected.category-name {
  color: #0489e0;
  font-weight: bold;
}
.subcategory.category-name {
  padding-left: 8px;
}
@media screen and (max-width: 480px) {
  .category-name {
    font-size: 12px;
  }
}
</style>
